import { AxiosRequestConfig } from 'axios';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'redux/hooks';
// Same signature, answered by the new backend. See legacy-deal-api.ts.
import {
  postCreateDeal,
  PostCreateDealRequestType,
} from 'service/moneeflo/legacy-deal-api';
import { toast } from 'sonner';
import { setcal_total_payable } from '../../../redux/features/CreateDeal';

interface SubmissionContext {
  channelOfInteraction: string;
  l2Partner?: number;
  state?: any;
}

export const useDealSubmission = () => {
  const dispatch = useDispatch();
  
  const {
    purpose_id_copy,
    currency,
    amount,
    type,
    country,
    discount,
    add_on_rates,
    remitter_name,
    customer_email,
    customer_phone,
    remitterPAN,
    remitterDOB,
    student_name,
    service_partner_mode,
    settlement_rates,
    service_partner_id,
    foreign_bank_rates,
    tt_rates,
    partner_margin,
    partner_walkin_margin,
    fx_charges,
    panBinary,
    aadhaarBinary,
    created_by,
    branch
  } = useAppSelector((state) => state.CreateDeal);

  const handleSubmitDeal = async (formData: any, context: SubmissionContext) => {
    const auth = localStorage.getItem("authToken");
    if (!auth) throw new Error("No auth token");
    
    const authToken = JSON.parse(auth);
    const data = new FormData();
    
    let newCreatedBy;
    if (context.state?.partnerId) {
      newCreatedBy = context.state.partnerId;
    } else {
      newCreatedBy = created_by;
    }

    // Append basic data
    data.append("purpose_id", purpose_id_copy);
    data.append("currency", currency);
    data.append("amount", amount);
    data.append("type", type);
    data.append("country", country);
    data.append("created_by", newCreatedBy as string);
    data.append("tcs_type", "1");
    data.append("discount", discount);
    data.append("addon_rates", isNaN(add_on_rates) ? "" : add_on_rates.toString());
    data.append("country", country);

    // Append customer data
    data.append("remitter_name", remitter_name);
    data.append("customer_email", customer_email);
    data.append("customer_phone", customer_phone);
    data.append("remitter_PAN", remitterPAN);
    data.append("remitter_DOB", remitterDOB);
    data.append("assigned_branch", branch);
    
    if (student_name) {
      data.append(
        "additional_detail",
        JSON.stringify({
          name: {
            value: student_name,
            type: "student",
          },
        })
      );
    }

    // Append contact/opportunity data
    if (context.state?.contactId || context.state?.opportunityId) {
      data.append("contacts_id", (context.state?.contactId || "0").toString());
      data.append("opportunity_id", (context.state?.opportunityId).toString());
    }

    data.append("channel_of_interaction_id", context.channelOfInteraction);

    // Handle service partner specific data
    if (service_partner_id === 20) {
      data.append("pan_card", panBinary);
      data.append("aadhar_card", aadhaarBinary);
    }

    // Handle settlement rates based on service partner mode
    if (service_partner_mode === "offline" && settlement_rates !== "") {
      data.append("service_partner_id", service_partner_id?.toString());
      data.append("settlement_rates", settlement_rates?.toString());
      data.append("foreign_bank_charges", foreign_bank_rates.toString());
      data.append("tt_charges", tt_rates?.toString());
    } else if (service_partner_mode === "online" && settlement_rates !== "") {
      data.append("service_partner_id", service_partner_id?.toString());
      data.append("settlement_rates", settlement_rates?.toString());
      data.append("foreign_bank_charges", foreign_bank_rates.toString());
      data.append("tt_charges", tt_rates?.toString());
    } else if (service_partner_mode === "online" && settlement_rates === "") {
      data.append("service_partner_id", service_partner_id?.toString());
    } else if (service_partner_mode === "offline" && settlement_rates === "") {
      data.append("service_partner_id", service_partner_id?.toString());
      data.append("foreign_bank_charges", foreign_bank_rates.toString());
      data.append("tt_charges", tt_rates?.toString());
    }

    data.append("partner_margin", partner_margin?.toString());
    data.append("partner_walkin_margin", partner_walkin_margin?.toString());
    data.append("fx_charges", fx_charges);

    // A settlement rate the desk has already agreed with the partner. It
    // is what the deal is priced at, and knowing it is what confirms the
    // deal, so it is booked confirmed rather than waiting for a call that
    // has already happened.
    const level = localStorage.getItem("level");
    if (level === "1" && formData.customSettlementRate) {
      data.append("settlement_rates", formData.customSettlementRate);
      data.append("already_settled", "true");
    } else if (service_partner_mode === "offline" && settlement_rates !== "") {
      data.append("settlement_rates", settlement_rates?.toString());
    }

    const payload: AxiosRequestConfig<PostCreateDealRequestType> = {
      headers: { Authorization: `Bearer ${authToken}` },
      data: data as any,
    };

    try {
      const res = await postCreateDeal(payload);
      localStorage.setItem("deal_id", JSON.stringify(res.data.deals.id));
      dispatch(setcal_total_payable(res.data.payment));
      return res.data;
    } catch (err: any) {
      toast.warning(err?.response?.data?.message || 'Deal creation failed');
      throw err;
    }
  };

  return {
    handleSubmitDeal,
  };
};